// model.js — data shapes and normalisation shared by the app, the db layer
// and the stats page. Everything stored (localStorage or Firestore) passes
// through here so that dates are always ISO strings and durations are always
// available as a number of seconds, whatever format older records used.

export const SCHEMA_VERSION = 2;

// Any date-ish value -> ISO string, or null.
// Handles: ISO/Date strings, Date objects, Firestore Timestamps (with toDate()
// or as plain {seconds, nanoseconds}), and epoch numbers (ms or s).
export function toISO(v) {
  if (v === null || v === undefined || v === '') return null;
  try {
    if (typeof v === 'string') { const d = new Date(v); return isNaN(d) ? null : d.toISOString(); }
    if (v instanceof Date) return isNaN(v) ? null : v.toISOString();
    if (typeof v.toDate === 'function') return v.toDate().toISOString();
    if (typeof v.seconds === 'number') {
      return new Date(v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6)).toISOString();
    }
    if (typeof v === 'number' && isFinite(v)) return new Date(v > 1e12 ? v : v * 1000).toISOString();
  } catch (e) { /* fall through */ }
  return null;
}

// "(h:mm)" -> minutes, or null.
export function parseDurationStr(s) {
  const m = typeof s === 'string' ? s.match(/\((\d+):(\d{1,2})\)/) : null;
  return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
}

// seconds -> "(h:mm)" (whole minutes, matching the historical display format)
export function formatDuration(sec) {
  const mins = Math.floor(Math.max(0, sec || 0) / 60);
  return `(${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, '0')})`;
}

// Seconds for a completed record: the numeric field when present, otherwise
// derived from the legacy "(h:mm)" string. null when unknown.
export function durationSecOf(c) {
  if (c && typeof c.durationSec === 'number' && isFinite(c.durationSec)) return c.durationSec;
  const mins = parseDurationStr(c && c.duration);
  return mins === null ? null : mins * 60;
}

// Local calendar date as YYYY-MM-DD (never UTC).
export function localDateStr(d) {
  if (!(d instanceof Date) || isNaN(d)) return '';
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

// Deterministic id for a legacy record that has none: same input, same id,
// so re-running a migration can never create duplicates.
export function legacyId(kind, item, index) {
  const s = kind + '|' + index + '|' + JSON.stringify(item);
  let h1 = 0x811c9dc5, h2 = 0x01000193;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 16777619) >>> 0;
    h2 = Math.imul(h2 + c, 2246822519) >>> 0;
  }
  return `legacy_${kind}_${h1.toString(16).padStart(8, '0')}${h2.toString(16).padStart(8, '0')}`;
}

function isObj(x) { return x !== null && typeof x === 'object'; }

// Older versions HTML-escaped task text on input and then rendered it with
// textContent, so "R&D" was stored and shown as "R&amp;D". Undo that once.
export function unescapeLegacyText(s) {
  if (typeof s !== 'string' || s.indexOf('&') === -1) return s;
  return s.replace(/&lt;/g, '<').replace(/&amp;/g, '&');
}

// Threshold (seconds) above which a completed duration is "much longer than
// usual" for this user: exp(mean + 2·SD) of the natural log of their timed
// completions (log-space, because durations are right-skewed).
// Guards: fewer than MIN_SAMPLE timed completions → fixed 8 h; never below 2 h.
export const LONG_DURATION_MIN_SAMPLE = 10;
export const LONG_DURATION_FALLBACK_SEC = 8 * 3600;
export const LONG_DURATION_FLOOR_SEC = 2 * 3600;
export function longDurationThresholdSec(completed) {
  const logs = [];
  for (const c of completed || []) {
    const sec = durationSecOf(c);
    if (sec !== null && sec >= 60) logs.push(Math.log(sec));
  }
  if (logs.length < LONG_DURATION_MIN_SAMPLE) return LONG_DURATION_FALLBACK_SEC;
  const mean = logs.reduce((a, b) => a + b, 0) / logs.length;
  const variance = logs.reduce((a, b) => a + (b - mean) ** 2, 0) / logs.length;
  const threshold = Math.exp(mean + 2 * Math.sqrt(variance));
  return Math.max(LONG_DURATION_FLOOR_SEC, Math.round(threshold));
}

export function formatHoursMinutes(sec) {
  const m = Math.round(sec / 60);
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}

// Ids of records deleted on this account, kept so another device's stale
// local mirror cannot re-add them. Bounded; oldest fall off.
export const DELETED_IDS_MAX = 200;
export function rememberDeleted(list, id) {
  const out = (list || []).filter(x => x !== id);
  out.push(id);
  return out.length > DELETED_IDS_MAX ? out.slice(out.length - DELETED_IDS_MAX) : out;
}

// Open (or queued) task -> canonical shape.
export function normalizeTask(t, fallbackId) {
  if (!isObj(t)) return null;
  const skips = Array.isArray(t.skips) ? t.skips.map(toISO).filter(Boolean) : [];
  const out = {
    id: t.id || fallbackId,
    text: unescapeLegacyText(typeof t.text === 'string' ? t.text : String(t.text ?? '')),
    tag: typeof t.tag === 'string' ? t.tag : '',
    createdOn: toISO(t.createdOn),
    skips
  };
  if (Array.isArray(t.skipReasons)) out.skipReasons = t.skipReasons; // future Growth Mode data, preserved
  return out;
}

// Completed record -> canonical shape. Keeps the "(h:mm)" string (older code
// and the display use it) and adds durationSec.
export function normalizeCompleted(c, fallbackId) {
  if (!isObj(c)) return null;
  const durationSec = durationSecOf(c);
  return {
    id: c.id || fallbackId,
    text: unescapeLegacyText(typeof c.text === 'string' ? c.text : String(c.text ?? '')),
    tag: typeof c.tag === 'string' ? c.tag : '',
    duration: typeof c.duration === 'string' ? c.duration : (durationSec === null ? '' : formatDuration(durationSec)),
    durationSec,
    createdOn: toISO(c.createdOn),
    startedOn: toISO(c.startedOn),
    completedOn: toISO(c.completedOn)
  };
}

// Plain JSON data: no undefined, no Date objects, no class instances.
export function plain(obj) { return JSON.parse(JSON.stringify(obj)); }
